/**
 * PlaybookList — project-scoped list of playbooks.
 * Columns: name, attached alarms, actions, armed status, last-run proxy, row menu.
 * Armed rows get a 3px left accent bar. EmptyState with safety note.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Workflow, Plus, Search } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import { Button, Badge, Input, Skeleton } from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  usePlaybooks,
  useDeletePlaybook,
  useAlarms,
  useWatchpoints,
  type Playbook,
} from '@/api/healthcheck';
import { fmtRelTime } from '../shared';
import { ACTION_META, type PlaybookActionType } from './action-type-meta';
import { RowMenu } from '../../components/RowMenu';

export function PlaybookList() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const { show } = useToast();

  const { data: playbooks = [], isLoading, isError, refetch } = usePlaybooks(projectId);
  const { data: alarmItems = [] } = useAlarms();
  const { data: wpData, isLoading: wpLoading } = useWatchpoints(projectId, {});

  // Project watchpoint IDs → filter alarms to this project.
  // Gate on wpData loaded to avoid a flash of 0 counts while watchpoints fetch.
  const projectWpIds = useMemo(
    () => new Set((wpData?.items ?? []).map((w) => w.id)),
    [wpData],
  );
  const projectAlarms = useMemo(
    () => (wpLoading ? [] : alarmItems.filter((a) => projectWpIds.has(a.watchpoint_id))),
    [alarmItems, projectWpIds, wpLoading],
  );

  // playbook_id → attached alarm count
  const alarmCountByPlaybook = useMemo(() => {
    const map = new Map<string, number>();
    for (const alarm of projectAlarms) {
      if (alarm.playbook_id) {
        map.set(alarm.playbook_id, (map.get(alarm.playbook_id) ?? 0) + 1);
      }
    }
    return map;
  }, [projectAlarms]);

  // playbook_id → latest incident triggered_at (proxy for "last run")
  // We skip full event fetch on list page to avoid N+1; show "—" until Detail.

  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const filtered = q ? playbooks.filter((p) => p.name.toLowerCase().includes(q)) : playbooks;

  const deletePlaybook = useDeletePlaybook();
  const [pendingDelete, setPendingDelete] = useState<Playbook | null>(null);

  const columns = useMemo<ColumnDef<Playbook, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => {
          const pb = row.original;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              {pb.is_active && (
                <div
                  style={{
                    width: 3,
                    height: 20,
                    borderRadius: 'var(--vn-radius-xs)',
                    background: 'var(--vn-success-solid)',
                    flexShrink: 0,
                  }}
                />
              )}
              <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'] }}>
                {pb.name}
              </span>
            </div>
          );
        },
      },
      {
        id: 'triggers',
        header: 'Triggers / Alarms',
        enableSorting: false,
        cell: ({ row }) => {
          const count = alarmCountByPlaybook.get(row.original.id) ?? 0;
          return count > 0 ? (
            <Badge tone="neutral" appearance="subtle" size="sm">
              {count} alarm{count === 1 ? '' : 's'}
            </Badge>
          ) : (
            <Badge tone="warning" appearance="subtle" size="sm">
              Not attached
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const acts = row.original.actions ?? [];
          const shown = acts.slice(0, 3);
          const extra = acts.length - shown.length;
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {shown.map((a, i) => {
                const meta = ACTION_META[a.type as PlaybookActionType];
                return (
                  <Badge key={i} tone={meta?.destructive ? 'danger' : 'neutral'} appearance="subtle" size="sm">
                    {meta?.label ?? a.type}
                  </Badge>
                );
              })}
              {extra > 0 && (
                <Badge tone="neutral" appearance="subtle" size="sm">+{extra}</Badge>
              )}
              {acts.length === 0 && (
                <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>—</span>
              )}
            </div>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const pb = row.original;
          return (
            <StatusPill tone={pb.is_active ? 'success' : 'neutral'}>
              {pb.is_active ? 'Armed' : 'Disarmed'}
            </StatusPill>
          );
        },
      },
      {
        id: 'updated',
        header: 'Updated',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            {fmtRelTime(row.original.updated_at)}
          </span>
        ),
      },
      {
        id: 'menu',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <RowMenu
            items={[
              { label: 'View / Edit', onSelect: () => navigate(`${base}/playbooks/${row.original.id}`) },
              { label: 'Delete', danger: true, onSelect: () => setPendingDelete(row.original) },
            ]}
          />
        ),
      },
    ],
    [alarmCountByPlaybook, navigate, base],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Workflow size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>Monitoring</Link></BreadcrumbItem>
            <BreadcrumbItem current>Playbooks</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Playbooks"
        description="Automated remediation runbooks that run when a linked alarm fires."
        actions={
          <Button
            size="sm"
            startIcon={<Plus size={15} />}
            onClick={() => navigate(`${base}/playbooks/create`)}
          >
            New playbook
          </Button>
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Couldn't load playbooks"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The monitoring service is temporarily unavailable.
        </Alert>
      ) : isLoading ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <>
          <Toolbar
            start={
              <Input
                placeholder="Search playbooks…"
                startSlot={<Search />}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ maxWidth: 320 }}
              />
            }
          />
          <DataTable
            columns={columns}
            data={filtered}
            getRowId={(p) => p.id}
            onRowClick={(p) => navigate(`${base}/playbooks/${p.id}`)}
            emptyState={
              playbooks.length === 0 ? (
                <EmptyState
                  icon={<Workflow size={28} />}
                  title="Automate your incident response"
                  description={
                    <>
                      Build a runbook that auto-mitigates threats the moment a linked alarm fires.
                      <br />
                      Playbooks are created disarmed — nothing runs against live traffic until you arm.
                    </>
                  }
                  actions={
                    <Button
                      size="sm"
                      startIcon={<Plus size={14} />}
                      onClick={() => navigate(`${base}/playbooks/create`)}
                    >
                      Create your first playbook
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<Workflow size={28} />}
                  title="No matching playbooks"
                  description="Try a different search term."
                />
              )
            }
            footer={
              filtered.length
                ? `${filtered.length} playbook${filtered.length === 1 ? '' : 's'}`
                : undefined
            }
          />
        </>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        destructive
        title={`Delete "${pendingDelete?.name}"?`}
        description="This playbook will be permanently deleted. Alarms linked to it will be detached automatically."
        confirmLabel="Delete"
        loading={deletePlaybook.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          const pb = pendingDelete;
          deletePlaybook.mutate(pb.id, {
            onSuccess: () => {
              show({ tone: 'success', title: `Deleted "${pb.name}"` });
              setPendingDelete(null);
            },
            onError: (e) => {
              show({ tone: 'danger', title: (e as Error)?.message || 'Delete failed' });
              setPendingDelete(null);
            },
          });
        }}
      />
    </Container>
  );
}

